import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect } from "react";
import { Loader } from "@/components/loader";
import { config } from "@/config";
import { useFetchSuppliers, useGlobalPermissions } from "@/domain/usersAndProjects/queryHooks";
import { handleAuthError, isAuthError } from "@/lib/auth/authErrorHandler";
import { userQueryKeys } from "@/lib/react-query/keyFactory";
import { v2UsersMe } from "@/usersAndProjects-client";
import type { AuthContextType, User } from "./authProvider.types";
import { useConfigCatFlags } from "./useConfigCatFlags";

export { useFeatureFlags } from "./useFeatureFlags";

const AuthContext = createContext<AuthContextType | null>(null);

interface Props {
  children: ReactNode;
}
export function AuthProvider({ children }: Props) {
  const { data, error, isLoading } = useQuery({
    queryKey: userQueryKeys.detail("me"),
    queryFn: async () => {
      const response = await v2UsersMe();
      return response.data;
    },
  });

  // Map the v2 /me shape (flattened, with `tenant`) onto the app's User model.
  const user: User | null = data
    ? {
        sub: data.sub,
        displayName: data.displayName,
        mail: data.mail ?? "",
        tenantId: data.tenant.id,
        tenantSlug: data.tenant.slug,
      }
    : null;

  // Supplier groups now come from GET /api/v2/groups?role=supplier — the v2 /me
  // response carries membership but no per-group roles, so intersect both sets.
  const { suppliers } = useFetchSuppliers({ enabled: !!user });
  const userGroupIds = new Set(data?.groups.map((group) => group.id) ?? []);
  const supplierGroups = user
    ? (suppliers ?? []).filter((group) => userGroupIds.has(group.id)).map((group) => group.id)
    : [];

  // Fetch global permissions (only when user is authenticated)
  const { permissions } = useGlobalPermissions({ enabled: !!user });

  // Fetch feature flags from ConfigCat (only when user is authenticated)
  const featureFlags = useConfigCatFlags({
    user,
    sdkKey: config.configCat.sdkKey,
  });

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch authenticated user:", error);

      // Handle authentication errors (401) by redirecting to login
      // Note: Also treating 500 errors from auth service as auth failures (may need revisiting)
      const shouldRedirectToLogin = isAuthError(error) || error.message?.includes("500");

      if (shouldRedirectToLogin) {
        handleAuthError();
      }
    }
  }, [error]);

  // Block child rendering until auth status is resolved.
  // This prevents downstream components from firing API calls before
  // we know if the user is authenticated, avoiding spurious 500 errors.
  if (isLoading) {
    return <Loader />;
  }

  const authContext: AuthContextType = { user, permissions, featureFlags, supplierGroups };

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
